// The dashboard's two variable sets — light (default) and dark. Light
// borrows a THEME_PRESETS entry (see DASHBOARD_LIGHT_PRESET_ID below),
// derived with the exact same contrast-verified math themeVars() uses for
// presets on the landing page, via the shared src/theme/themeMath.js.
//
// Both objects use the SAME --color-* variable names tailwind.config.js and
// src/theme/dashboardTheme.css declare — this is what lets every existing
// bg-panel/text-bone/border-line class in the ~15 dashboard pages resolve
// correctly in either theme with zero className changes: only which of
// these two objects is applied (as inline `style` on the dashboard's inner
// main-content wrapper — see App.jsx) changes.
//
// Deliberately a distinct --color-* namespace from themeVars()'s --t-*
// names: the two systems are consumed by different selector scopes
// (.subscene-dashboard vs .subscene-landing) and must never collide if a
// dashboard page and a landing section are ever in the same DOM.
import { mix, rgbChannels, contrast, readableAccent } from "./themeMath";
import { themeById } from "./tokens";
import { clipVars } from "./clipColors";

// Which THEME_PRESETS entry (tokens.js) the dashboard's light theme uses.
// Swap the id to try a different one.
export const DASHBOARD_LIGHT_PRESET_ID = "paper-mint";

// Mirrors src/theme/dashboardTheme.css's :root block exactly — kept here too
// so DashboardThemeContext can apply it as an explicit inline `style` when
// dark mode is active (rather than relying on inline `style` being absent,
// which would make "dark" and "no theme chosen yet" indistinguishable).
export const dashboardDarkVars = {
  "--color-ink": "#08090A",
  "--color-ink-raised": "#0C0E10",
  // Lifted from #101215/#15181C. A card at #101215 separated from the #08090A
  // page by only 1.06:1 — with no shadow and a 14% border, it barely read as
  // a surface at all. panel-hi keeps its step above panel (raised controls,
  // inputs, pills sit on it), so both move together rather than crossing.
  "--color-panel": "#16191D",
  "--color-panel-hi": "#1C2025",
  "--color-volt": "#C6FF3A",
  "--color-volt-hover": "#A8E01A",
  "--color-volt-rgb": "198, 255, 58",
  "--color-signal": "#39FF88",
  "--color-signal-rgb": "57, 255, 136",
  // Pushed brighter than the landing page's PAGE.bone/mute/muteDim on
  // purpose: those were tuned as body copy on a page you read once, but
  // these are the dashboard's smallest, most re-read text (labels, meta,
  // captions) — low contrast there reads as "washed out" much faster.
  "--color-bone": "#F5F7F1",
  "--color-mute": "#C6CFC0",
  "--color-mute-dim": "#A8B2A0",
  "--color-line": "rgba(198, 255, 58, 0.20)",
  "--color-line-soft": "rgba(236, 239, 232, 0.08)",
  "--color-line-grid": "rgba(198, 255, 58, 0.26)",
  // Elevation. On a near-black backdrop a drop shadow alone is nearly
  // invisible, so the inset top hairline does the real work — it reads as a
  // lit top edge and lifts the card in a way no outer shadow can. The two
  // outer layers add weight underneath without muddying the near-black page.
  "--color-shadow-card":
    "inset 0 1px 0 0 rgba(236, 239, 232, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.60), 0 8px 24px -6px rgba(0, 0, 0, 0.75)",
  // The lifted-card hover state. Deeper and wider than the resting shadow so
  // a card that is ALREADY raised still visibly answers the cursor.
  "--color-shadow-card-hover":
    "inset 0 1px 0 0 rgba(236, 239, 232, 0.09), 0 2px 4px 0 rgba(0, 0, 0, 0.65), 0 18px 40px -10px rgba(0, 0, 0, 0.85)",
  // Status-pill text (WorkflowHistory's Recent Activity cards: "Awaiting
  // review"/"Failed"/"Script only" etc.) — a pale Tailwind -300 reads fine on
  // a near-black pill background but is nearly invisible on a light one, so
  // this needs its own dark/light pair rather than a fixed Tailwind class.
  // Dark theme keeps the original pale -300 weight; light theme uses a
  // saturated -800 for contrast against the same translucent /20 background.
  "--color-amber-text": "#FCD34D",
  "--color-red-text": "#FCA5A5",
  "--color-sky-text": "#7DD3FC",
  "--color-blue-text": "#93C5FD",

  // The five timeline track colours. Spread IDENTICALLY into both var sets on
  // purpose: the editor is dark in both themes (see editorTheme.js), so these
  // always sit on the same dark surfaces. Running them through readableAccent
  // against a cream panel they never touch would push them off-hue for no gain.
  // Do not "fix" this into a derived light variant.
  ...clipVars,
};

// Writes dashboardDarkVars into a real `:root { ... }` rule at module load —
// this IS the CSS fallback that used to be hand-duplicated as static values
// in src/theme/dashboardTheme.css (that file now only carries the doc
// comment; no color literals). Needed because a few screens render with no
// themed ancestor at all (the pre-auth Suspense fallback and loading spinner
// in App.jsx, both outside DashboardThemeProvider), so `:root` must already
// hold these values the instant the stylesheet is parsed — before React
// mounts anything.
//
// ALWAYS dark, never the light default, and deliberately not reading the
// user's saved theme preference: `:root` is the base layer both the themed
// main-content wrapper AND the Sidebar inherit from (Sidebar has no local
// override of its own — see App.jsx, it's a sibling outside
// DashboardThemeProvider's scope). The main content area's actual default
// (light) is applied by DashboardThemeContext as a local inline `style` on
// just that wrapper; `:root` staying dark is what keeps Sidebar dark
// regardless of which theme the rest of the dashboard is in. Editing
// dashboardDarkVars above is the only edit needed; this keeps the CSS
// fallback honest automatically instead of requiring a second hand-edit in a
// second file.
if (typeof document !== "undefined") {
  const styleEl = document.createElement("style");
  styleEl.setAttribute("data-dashboard-theme", "dark-root-defaults");
  styleEl.textContent = `:root {\n${Object.entries(dashboardDarkVars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n")}\n}`;
  document.head.appendChild(styleEl);
}

// Computed once at module load (the preset's hex values are static), not
// per-render — themeById/mix/contrast are all pure and cheap, but there's no
// reason to redo the walk-for-contrast loop in readableAccent() on every
// theme toggle when the input never changes.
export const dashboardLightVars = (() => {
  const t = themeById(DASHBOARD_LIGHT_PRESET_ID);
  const step = 0.05;
  const raised = mix(t.backgroundColor, t.textColor, step * 0.7);
  const panel = mix(t.backgroundColor, t.textColor, step);
  const panelHi = mix(t.backgroundColor, t.textColor, step * 1.6);

  // The true archival orange (~3.5:1 as text on its own panel) gets walked
  // toward the text color until it clears 4.5:1 — this walked value becomes
  // --color-volt itself (not a second variable), so every existing
  // text-volt/bg-volt/border-volt class in the app stays contrast-safe in
  // light mode without any component needing to change which class it uses.
  const accentSafe = readableAccent(t.accentColor, panelHi, t.textColor);

  // readableAccent() hardcodes a 4.5 floor and walks in coarse 0.05 steps, so
  // it stops at the FIRST candidate over the line — which left mute at 4.54:1
  // on panelHi, the weakest value in the system. That tier carries real body
  // copy (plan features, package and card descriptions) at 14px, so it needs
  // AAA headroom and a finer walk. Same mix-toward-text strategy, just
  // parameterised by floor.
  //
  // Giving muteDim its own LOWER floor is also what fixes a polarity
  // inversion: it used to be mix(mutedSafe, text, 0.35), i.e. walked further
  // toward the text color than mute was, so the "dimmer" tier measured 6.56:1
  // against mute's 4.54:1 — more legible than the tier it is meant to sit
  // under, and backwards from dark mode. Deriving both from the same source
  // against different floors keeps mute the stronger of the two by
  // construction.
  const walkToFloor = (from, surface, toward, floor) => {
    if (contrast(from, surface) >= floor) return from;
    for (let i = 1; i <= 40; i += 1) {
      const candidate = mix(from, toward, i * 0.025);
      if (contrast(candidate, surface) >= floor) return candidate;
    }
    return toward;
  };

  const mutedSafe = walkToFloor(t.mutedTextColor, panelHi, t.textColor, 7.0);
  const mutedDimSafe = walkToFloor(t.mutedTextColor, panelHi, t.textColor, 5.5);

  const accentRgb = rgbChannels(accentSafe);
  const textRgb = rgbChannels(t.textColor);

  return {
    "--color-ink": t.backgroundColor,
    "--color-ink-raised": raised,
    "--color-panel": panel,
    "--color-panel-hi": panelHi,
    "--color-volt": accentSafe,
    "--color-volt-hover": mix(accentSafe, t.textColor, 0.15),
    "--color-volt-rgb": accentRgb,
    "--color-signal": accentSafe,
    "--color-signal-rgb": accentRgb,
    "--color-bone": t.textColor,
    "--color-mute": mutedSafe,
    // Derived against a lower floor than mute (5.5 vs 7.0) rather than by
    // walking mute further — see walkToFloor above for why that ordering
    // matters.
    "--color-mute-dim": mutedDimSafe,
    // A 14% hairline that reads fine on near-black nearly disappears at the
    // same alpha on cream, so --color-line/--color-line-soft (single
    // hairlines: card borders, rule-tops) do need a stronger alpha here.
    //
    // --color-line-grid is different: it's a *repeating full-viewport*
    // pattern (GridTexture, App.jsx), not a single line, so the same alpha
    // that looks right for one hairline reads as visually dense once
    // multiplied across dozens of crossing lines. themeVars() on the
    // landing page uses a flat 0.55 for archival's grid, but that's tuned
    // for one card-sized decorative section, not a persistent full-page
    // background — reusing it verbatim here is what made the dashboard's
    // light grid overwhelming. 0.16 keeps its visual weight close to dark
    // mode's own 0.14 (see dashboardDarkVars above) rather than matching
    // the hairline alpha.
    "--color-line": `rgba(${accentRgb}, 0.30)`,
    "--color-line-soft": `rgba(${textRgb}, 0.16)`,
    "--color-line-grid": `rgba(${accentRgb}, 0.26)`,
    // Elevation. Tinted with the theme's own ink rather than neutral black so
    // it sits in the palette; three stacked layers (contact + mid + ambient)
    // read as physically lifted where a single layer reads as a grey smudge.
    "--color-shadow-card":
      `0 1px 2px 0 rgba(${textRgb}, 0.06), 0 4px 12px -2px rgba(${textRgb}, 0.10), 0 12px 28px -8px rgba(${textRgb}, 0.10)`,
    // See dashboardDarkVars for why hover needs its own, deeper value.
    "--color-shadow-card-hover":
      `0 2px 4px 0 rgba(${textRgb}, 0.08), 0 8px 20px -4px rgba(${textRgb}, 0.14), 0 20px 44px -12px rgba(${textRgb}, 0.16)`,
    // Dark, saturated -800 weights instead of dark theme's pale -300 — see
    // dashboardDarkVars for why these need their own pair. Measured against
    // paper-mint's panelHi (~#d9e0db, close enough to the translucent /20
    // badge backgrounds these sit on): amber 5.28:1, red 6.19:1, sky 5.63:1,
    // blue 6.49:1.
    "--color-amber-text": "#92400E",
    "--color-red-text": "#991B1B",
    "--color-sky-text": "#075985",
    "--color-blue-text": "#1E40AF",

    // The five timeline track colours. Spread IDENTICALLY into both var sets on
    // purpose: the editor is dark in both themes (see editorTheme.js), so these
    // always sit on the same dark surfaces. Running them through readableAccent
    // against a cream panel they never touch would push them off-hue for no gain.
    // Do not "fix" this into a derived light variant.
    ...clipVars,
  };
})();
